import React from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import EmailField from '@/components/form/email/EmailField';
import { Button } from '@/components/buttons/Button';
import Form from '@/components/form/Form';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import BffApiService from '@/shared/bffApiService';
import PromptMessage from '@/components/UI/PromptMessage';
import { useLogin } from '@/hooks/useLogin';
import { generateBffErrorWithTranslationId } from '@/utils/utils';

const initialValues = {
  email: '',
};

const ForgotPasswordForm = ({ onSuccessCallback }: { onSuccessCallback: () => void }) => {
  const intl = useIntl();
  const { request, loading, error } = useBffApiRequest();
  const { openLogin } = useLogin();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .email(intl.formatMessage({ id: 'inputs.email.invalid' })),
  });

  const successCallback = () => {
    triggerGTag(GTagEvents.forgot_password_success);
    onSuccessCallback();
  };

  const errorCallback = () => {
    triggerGTag(GTagEvents.forgot_password_error);
  };

  const handleSubmit = ({ email }: { email: string }) => {
    triggerGTag(GTagEvents.forgot_password_submit);
    request({
      apiMethod: BffApiService.forgotPassword,
      payload: { email },
      successCallback,
      errorCallback,
    });
  };

  const handleSwitchAuthMethod = () => {
    openLogin({ openedFrom: OpenedFrom.ForgotPassword });
  };

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col w-full justify-between gap-7"
    >
      {() => (
        <>
          <EmailField
            data-cy="forgot-password-form-email"
            label={intl.formatMessage({ id: 'inputs.email' })}
            name="email"
            className="text-white focus-within:border-blue-light bg-white/20"
          />
          <div className="grid grid-cols-1 gap-4 mt-4">
            {error && (
              <PromptMessage
                type="error"
                message={generateBffErrorWithTranslationId(intl, 'error.support', error.code)}
              />
            )}
            <Button
              text={intl.formatMessage({ id: 'authentication.forgot-password.button' })}
              type="submit"
              btnType="primary"
              data-cy="forgot-password-submit-button"
              isLoading={loading}
            />
            <div className="text-white text-md text-center">
              <FormattedMessage
                id="authentication.forgot-password.change-to-login"
                values={{
                  a: str => (
                    <span
                      onClick={handleSwitchAuthMethod}
                      className="text-blue-light font-bold underline cursor-pointer"
                    >
                      {str}
                    </span>
                  ),
                }}
              />
            </div>
            <div className="text-xs italic text-white">
              {intl.formatMessage({ id: 'authentication.forgot-password.description' })}
            </div>
          </div>
        </>
      )}
    </Form>
  );
};

export default ForgotPasswordForm;
